import { devLog } from './devLog';

// Persists user-added firmware .bin URLs in localStorage so they survive a
// reload. Built-in production defaults still live in code (state/initialState.js);
// this store only holds the URLs a user has added through the UI. loadUrls()
// merges the two (defaults first, then persisted extras), de-duped.

const STORAGE_PREFIX = 'dontek.firmwareUrls.';

const storageKey = (key) => `${STORAGE_PREFIX}${key}`;

/**
 * The canonical form of a URL as it will be stored. Callers that mirror the
 * store in their own state must key off this, not their raw input, or the two
 * drift apart the moment a padded value is passed.
 * @param {unknown} url
 * @returns {string}
 */
export const normaliseFirmwareUrl = (url) => (typeof url === 'string' ? url.trim() : '');

/**
 * Read the persisted URL list for a category. Returns [] on any error or when
 * nothing has been stored yet.
 *
 * Entries are canonicalised on the way out, so every caller sees the same form
 * regardless of what is physically in localStorage — which is hand-editable and
 * may hold values written before saveUrl normalised. Without this, a padded
 * legacy entry would evade saveUrl's duplicate check (storing a second copy),
 * surface in the picker with its whitespace, and be sent to a controller as-is.
 *
 * @param {string} key category key, e.g. "psw", "aquachem", "powerboard"
 * @returns {string[]}
 */
export const readStoredUrls = (key) => {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const canonical = [];
    for (const entry of parsed) {
      if (typeof entry !== 'string') continue;
      const url = normaliseFirmwareUrl(entry);
      // Drop blanks, and collapse entries that differ only by whitespace.
      if (url && !canonical.includes(url)) canonical.push(url);
    }
    return canonical;
  } catch (error) {
    devLog('firmwareUrlStore.readStoredUrls failed', key, error);
    return [];
  }
};

const readStored = readStoredUrls;

/**
 * A firmware URL is only accepted if it looks like one: an absolute http(s)
 * URL pointing at a .bin image. Anything else would be handed to the
 * controller verbatim as an OTA source, so it's rejected rather than stored.
 *
 * http is allowed alongside https on purpose. OTA images are served from
 * plain-HTTP hosts on the LAN during development and field testing (this
 * app's own history used http://10.34.4.102:8000/…), and it is an
 * internal-only tool, so the plaintext risk has been judged acceptable here.
 * Schemes a controller cannot fetch — file:, data:, javascript: — are still
 * rejected, as is anything that isn't an absolute URL.
 *
 * Takes unknown, not string: it normalises through normaliseFirmwareUrl, which
 * turns any non-string into "" and fails the check below. Callers pass values
 * straight from pickers, props and localStorage, so narrowing this to string
 * would misreport what it accepts and produce false errors in the @ts-check
 * consumers that hand it a possibly-undefined selection.
 *
 * @param {unknown} url
 * @returns {{ valid: boolean, reason?: string }}
 */
export const validateFirmwareUrl = (url) => {
  const value = normaliseFirmwareUrl(url);
  if (!value) return { valid: false, reason: 'Enter a firmware URL.' };
  let parsed;
  try {
    parsed = new URL(value);
  } catch (error) {
    return { valid: false, reason: 'Not a valid URL.' };
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return { valid: false, reason: 'Firmware URLs must use http or https.' };
  }
  if (!parsed.pathname.toLowerCase().endsWith('.bin')) {
    return { valid: false, reason: 'Firmware URLs must point at a .bin file.' };
  }
  return { valid: true };
};

/**
 * The full URL list for a category: built-in defaults first, then any
 * user-added URLs not already present, de-duped in order.
 * @param {string} key
 * @param {string[]} [defaults]
 * @returns {string[]}
 */
export const loadUrls = (key, defaults = []) => {
  // Stored entries are filtered, not trusted: localStorage is hand-editable and
  // may hold values written before saveUrl validated, so an invalid URL could
  // otherwise reappear in the picker and be sent to a controller.
  //
  // Defaults are NOT filtered. They come from state/initialState.js, so an
  // invalid one is a code defect that should be visible in review and in the
  // UI — quietly hiding it here would turn a bad shipped URL into a firmware
  // list that silently comes up short. They are all https .bin today.
  const stored = readStored(key).filter((url) => validateFirmwareUrl(url).valid);
  const merged = [];
  for (const url of [...defaults, ...stored]) {
    if (url && !merged.includes(url)) merged.push(url);
  }
  return merged;
};

/**
 * Append a user-added URL to a category's persisted list (no-op if already
 * stored). Returns the updated persisted list.
 *
 * url is unknown for the same reason as validateFirmwareUrl: this is the only
 * door into persistence, so it deliberately accepts whatever a caller has and
 * rejects it here rather than trusting the type.
 *
 * @param {string} key
 * @param {unknown} url
 * @returns {string[]}
 */
export const saveUrl = (key, url) => {
  // Validate and normalise here, not just in the UI. This is the only door into
  // persistence, and anything that gets through can later be selected and sent
  // to a controller as an OTA source.
  const value = normaliseFirmwareUrl(url);
  if (!validateFirmwareUrl(value).valid) {
    devLog('firmwareUrlStore.saveUrl rejected', key, url);
    return readStored(key);
  }
  const stored = readStored(key);
  if (stored.includes(value)) return stored;
  const next = [...stored, value];
  try {
    window.localStorage.setItem(storageKey(key), JSON.stringify(next));
  } catch (error) {
    // Quota exceeded, storage disabled, private mode… Report what is actually
    // persisted, not what we wanted to persist: callers mirror this return
    // value into their own state, and claiming success would show the URL as
    // saved right up until it vanished on the next reload.
    devLog('firmwareUrlStore.saveUrl failed', key, url, error);
    return stored;
  }
  return next;
};

/**
 * Drop a user-added URL from a category's persisted list. Built-in defaults
 * live in code and are not stored, so removing one is a no-op — loadUrls()
 * would merge it straight back in.
 *
 * url is unknown for the same reason as saveUrl — it normalises before use.
 *
 * @param {string} key
 * @param {unknown} url
 * @returns {string[]} the updated persisted list
 */
export const removeUrl = (key, url) => {
  const value = normaliseFirmwareUrl(url);
  const stored = readStored(key);
  if (!stored.includes(value)) return stored;
  const next = stored.filter((u) => u !== value);
  try {
    window.localStorage.setItem(storageKey(key), JSON.stringify(next));
  } catch (error) {
    // As in saveUrl: report what is actually persisted. Claiming the removal
    // stuck would drop the URL from the picker only for it to reappear on the
    // next reload.
    devLog('firmwareUrlStore.removeUrl failed', key, url, error);
    return stored;
  }
  return next;
};

export const FIRMWARE_URL_KEYS = {
  psw: 'psw',
  psw_beta: 'psw_beta',
  aqualight: 'aqualight',
  aqualight_beta: 'aqualight_beta',
  aquachem: 'aquachem',
  controlboard: 'controlboard',
  powerboard: 'powerboard',
  ioboard: 'ioboard',
};
